import {
  useGetCompanyByIdQuery,
  useLazyGetBankAccountsListQuery,
  useLazyGetBranchesQuery,
} from '@/api'
import { PageHeader } from '@/components'
import { useModal, useRole } from '@/hooks'
import { ModalType, setCurrentHasBranches } from '@/store/app'
import {
  Box,
  Button,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { skipToken } from '@reduxjs/toolkit/dist/query'
import { createContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { TabPanel } from '../EditDictionarieVehicle/components'
import { CompanyStatus } from './components'
import { breadcrumbsItems } from './constants'
import { styles } from './styles'
import { Branches, Employees, GeneralInfo } from './Tabs'
import type { OrganizationErrorForms } from './types'
import { useCompany } from '@/hooks/useCompany'
import { useAppDispatch } from '@/store'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const OrganizationContext = createContext({} as any)

export const Organization = () => {
  const { organizationId, organizationTab } = useParams()
  const { showModal, modalOpened } = useModal()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { getAccess } = useRole()
  const { user } = useCompany()

  const { data: organization, refetch: refetchOrganization } =
    useGetCompanyByIdQuery(organizationId ? organizationId : skipToken)
  const [getBanks, { data: banks }] = useLazyGetBankAccountsListQuery()

  const [getBranches] = useLazyGetBranchesQuery()
  useEffect(() => {
    getBranches({
      companyId: Number(organizationId),
      params: {
        parentIds: 0,
      },
    }).then((response) => {
      dispatch(setCurrentHasBranches(Boolean(response?.data?.length)))
    })
  }, [])

  const [alignment, setAlignment] = useState<string>(
    organizationTab ?? 'general'
  )

  const [errorForms, setErrorForms] = useState<OrganizationErrorForms>({
    generalInfoErrors: 3,
    generalInfo: true,
    generalInfoAddress: true,
    generalInfoBankAccount: true,
    generalInfoIntegration: true,
    generalInfoShown: false,
  })

  const [costyl, setCostyl] = useState<number>(2)

  useMemo(() => {
    if (organization) {
      const generalInfo = [
        organization.title,
        organization.short_title,
        organization.inn,
        // organization.kpp,
        // organization.ogrn,
      ].every((item) => Boolean(item))
      const generalInfoAddress = [
        organization.phone_number,
        organization.legal_address,
        organization.phys_address,
      ].every((item) => Boolean(item))
      const generalInfoBankAccount = banks && banks?.data?.length > 0
      const generalInfoIntegration = Boolean(organization?.signatureSystemType)
      let errors = 0
      errors += generalInfo ? 0 : 1
      errors += generalInfoAddress ? 0 : 1
      errors += generalInfoBankAccount ? 0 : 1
      errors += generalInfoIntegration ? 0 : 1
      setErrorForms((prev) => ({
        ...prev,
        generalInfo: !generalInfo,
        generalInfoAddress: !generalInfoAddress,
        generalInfoBankAccount: !generalInfoBankAccount,
        generalInfoIntegration: !generalInfoIntegration,
        generalInfoErrors: errors,
      }))
      if (costyl === 0 && !errorForms.generalInfoShown) {
        setErrorForms((prev) => {
          if (generalInfo && generalInfoAddress && generalInfoBankAccount) {
            showModal(ModalType.ORGANIZATION_COMPLETE_GENERAL)
            return { ...prev, generalInfoShown: true }
          }
          return { ...prev, generalInfoShown: false }
        })
      } else {
        setCostyl((prev) => prev - 1)
      }
    }
  }, [organization, banks])

  const handleChange = (
    _: React.MouseEvent<HTMLElement>,
    newAlignment: string
  ) => {
    if (newAlignment) {
      setAlignment(newAlignment)
      navigate(`/organization/${organizationId}/${newAlignment}`, {
        replace: true,
      })
    }
  }

  return (
    <Box>
      <PageHeader
        title={organization?.short_title ?? 'Новая организация'}
        breadcrumbs={[
          ...breadcrumbsItems,
          {
            title: organization?.short_title
              ? `Организация ${organization?.short_title}`
              : 'Создание организации',
          },
          {
            title: organizationTab === 'employee' ? `Сотрудники` : '',
          },
        ]}
        // backUrl={ROUTE_PATH.ORGANIZATION}
        status={
          organization ? <CompanyStatus status={organization?.status} /> : null
        }
        buttons={
          <Stack direction={'row'} gap={2}>
            {/* {getAccess('ORGANIZATION', ['UPDATE', 'ADMIN']) && (
              <Button variant="outlined" startIcon={<ArchiveIcon />}>
                В архив
              </Button>
            )} */}
            <Button
              variant="contained"
              onClick={() => navigate(`/organization`)}
            >
              Перейти к списку организаций
            </Button>
            {/* {getAccess('ORGANIZATION', ['UPDATE', 'DELETE', 'ADMIN']) && (
              <Button variant="contained" color="error">
                <TrashIcon />
              </Button>
            )} */}
          </Stack>
        }
      />
      <Box
        sx={{
          backgroundColor: 'white',
          margin: '24px',
          padding: '24px',
          borderRadius: '4px',
          height: '100%',
        }}
      >
        <ToggleButtonGroup
          sx={styles.toggleButtonsGroup}
          color="primary"
          value={alignment}
          exclusive
          onChange={handleChange}
        >
          <ToggleButton size={'large'} value={'general'}>
            <Stack direction={'row'} gap={'8px'} alignItems={'center'}>
              Основное
              {/* {errorForms?.generalInfoErrors ? (
                <Chip
                  sx={styles.chip}
                  variant={'outlined'}
                  color="warning"
                  label={errorForms?.generalInfoErrors}
                />
              ) : null} */}
            </Stack>
          </ToggleButton>
          {organization && (
            <ToggleButton size={'large'} value={'employee'}>
              Сотрудники
            </ToggleButton>
          )}
          {organization && (
            <ToggleButton size={'large'} value={'branches'}>
              Филиалы
            </ToggleButton>
          )}
        </ToggleButtonGroup>
        <OrganizationContext.Provider
          value={{
            organization,
            banks,
            refetchOrganization,
            getBanks,
            errorForms,
            organizationId,
            user,
          }}
        >
          <TabPanel id={'general'} value={alignment}>
            <GeneralInfo />
          </TabPanel>
          <TabPanel id={'employee'} value={alignment}>
            <Employees />
          </TabPanel>
          <TabPanel id={'branches'} value={alignment}>
            <Branches />
          </TabPanel>
        </OrganizationContext.Provider>
      </Box>
      {/* <BankAccountComplete />
      <GeneralComplete /> */}
    </Box>
  )
}
